import React, { useEffect, useRef, useState } from "react";
import { createConnection, respondConnection } from "../../api/api";
import { usePeerConnectionStatus } from "../../context/UserRealtimeContext";
import { isAccountVerified } from "../utils";

const ACCEPTED_HIDE_MS = 3000;

/**
 * Connect / Pending / Accept Connection control for a post or event author.
 * Hidden for org viewers, org authors, self, and already-connected users.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @param {object} props.item - Post or event with author + connection fields
 * @param {string} [props.acceptLabel]
 * @param {() => void} [props.onAccepted]
 * @param {() => void} [props.onAcceptedHidden]
 * @returns {JSX.Element|null}
 */
export default function PostConnectButton({
  userId,
  user,
  item,
  acceptLabel = "Accept Connection",
  onAccepted,
  onAcceptedHidden,
}) {
  const authorId = item?.author_id;
  const authorIsOrg = !!item?.author_is_org;
  const isOrgViewer = user?.type === "organization";

  const { status, connectionId, reportConnectionChange, refreshPendingConnections } =
    usePeerConnectionStatus(
      authorId,
      item?.user_connection_status || "none",
      item?.connection_id || 0
    );
  const [busy, setBusy] = useState(false);
  const [showAccepted, setShowAccepted] = useState(false);
  const [prevStatus, setPrevStatus] = useState(status);
  const onAcceptedHiddenRef = useRef(onAcceptedHidden);
  onAcceptedHiddenRef.current = onAcceptedHidden;

  if (status !== prevStatus) {
    setPrevStatus(status);
    if (
      (status === "active" || status === "accepted") &&
      prevStatus === "pending_received"
    ) {
      setShowAccepted(true);
    } else if (status !== "active" && status !== "accepted") {
      setShowAccepted(false);
    }
  }

  useEffect(() => {
    if (!showAccepted) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setShowAccepted(false);
      onAcceptedHiddenRef.current?.();
    }, ACCEPTED_HIDE_MS);
    return () => clearTimeout(timer);
  }, [showAccepted]);

  if (
    !userId ||
    !authorId ||
    Number(userId) === Number(authorId) ||
    authorIsOrg ||
    isOrgViewer ||
    ((status === "active" || status === "accepted") && !showAccepted)
  ) {
    return null;
  }

  const invitesLocked = Boolean(user) && !isAccountVerified(user);

  const handleConnect = async () => {
    if (busy || status !== "none" || invitesLocked) return;
    setBusy(true);
    try {
      const result = await createConnection({
        sender_id: userId,
        receiver_id: authorId,
      });
      if (result.success) {
        reportConnectionChange({
          peerUserId: authorId,
          status: "pending_sent",
          connectionId: result.data?.id || 0,
        });
      } else {
        console.error(result.error || result.message || "Failed to connect");
      }
    } catch (err) {
      console.error("Error connecting:", err);
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async () => {
    if (busy || status !== "pending_received" || !connectionId) return;
    setBusy(true);
    try {
      const result = await respondConnection({
        connection_id: connectionId,
        user_id: userId,
        status: "active",
      });
      if (result.success) {
        reportConnectionChange({
          peerUserId: authorId,
          status: "active",
          connectionId,
        });
        await refreshPendingConnections();
        onAccepted?.();
      } else {
        console.error(result.error || result.message || "Failed to accept connection");
      }
    } catch (err) {
      console.error("Error accepting connection:", err);
    } finally {
      setBusy(false);
    }
  };

  if (invitesLocked && status === "none") {
    return null;
  }

  let label = "Connect";
  let onClick = handleConnect;
  let disabled = busy;

  if (status === "pending_sent") {
    label = "Pending";
    onClick = undefined;
    disabled = true;
  } else if (status === "pending_received") {
    label = acceptLabel;
    onClick = handleAccept;
  } else if (status === "accepted" || showAccepted) {
    label = "Accepted";
    onClick = undefined;
    disabled = true;
  } else if (status === "rejected" || status === "left") {
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="rounded border border-accent bg-ui-card px-3 py-1 text-sm text-accent hover:bg-highlight disabled:cursor-default disabled:opacity-60 disabled:hover:bg-ui-card"
    >
      {busy ? "…" : label}
    </button>
  );
}
